/**
 * MyAudits — the ONLY Audit surface a department-bound user sees (the
 * Sentinel MyRecordRequests pattern). Session-authed via /api/audit/my-audits,
 * which is scoped server-side to internal audits of the user's own department.
 * Renders the same request-and-respond cards as the public auditee portal,
 * through the shared AuditeeRequestList with a session fetch adapter.
 *
 * Internal audit reports (operator order 2026-10-27): the ODAG, Provider
 * Directory Accuracy and other program audits a user is named on (the
 * engagement notice or the draft report) are listed here too, from
 * /api/audit/my-audits/internal. Opening one shows the same response page
 * the emailed link opens, signed in: the notice, the draft report, and a
 * response form on every finding.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AuditeeRequestList from './AuditeeRequestList.jsx';
import AuditResponsePage from './AuditResponsePage.jsx';

const API = process.env.REACT_APP_API_URL;

const PROGRAM_NAMES = { pda: 'Provider Directory Accuracy', odag: 'ODAG', cdag: 'CDAG', snpcc: 'SNPCC', website: 'Website' };

/** Session-cookie fetch adapter for the shared request list. */
function internalAdapter(auditId) {
  return {
    respond: async (rid, text) => {
      const res = await fetch(`${API}/api/audit/my-audits/${auditId}/requests/${rid}/respond`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response_text: text }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'Your answer did not go through. Try again.');
      return d.request;
    },
    upload: async (rid, file) => {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`${API}/api/audit/my-audits/${auditId}/requests/${rid}/upload`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.message || 'The upload did not go through. Try again.');
      return d.document;
    },
  };
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

function AuditDetailView({ auditId, onBack }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/my-audits/${auditId}/requests`, { credentials: 'include' }).then((r) => r.json());
      if (r.success) setData(r);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-slate-400 py-6">Loading…</div>;
  const a = data.audit || {};
  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">← My audits</button>
      <div>
        <div className="text-base font-bold text-slate-800">{a.subject_name}</div>
        <div className="text-xs text-slate-400">
          {a.template_name}
          {a.evidence_due_date ? <> · documents due <b>{fmtDate(a.evidence_due_date)}</b></> : null}
        </div>
      </div>
      <AuditeeRequestList requests={data.requests || []} uploads={data.uploads || []} adapter={internalAdapter(auditId)} onChanged={load} />
    </div>
  );
}

/** One internal audit report: the response page, signed in. */
function InternalAuditView({ audit, onBack }) {
  return (
    <div className="space-y-4">
      <button type="button" onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700">← My audits</button>
      <AuditResponsePage embedded base={`${API}/api/audit/my-audits/internal/${audit.id}`} storageId={`internal:${audit.id}`} />
    </div>
  );
}

// What the card says about where the audit stands for this person.
function internalStatus(a) {
  if (a.report_final_at) return { label: 'Final report issued', cls: 'text-slate-500 bg-slate-50 border-slate-200' };
  if (!a.report_sent_at) return { label: a.sent_at ? 'Notice received · fieldwork under way' : 'Opened', cls: 'text-slate-500 bg-slate-50 border-slate-200' };
  const f = a.findings || { total: 0, responded: 0 };
  const left = Math.max(0, Number(f.total || 0) - Number(f.responded || 0));
  if (left > 0) return { label: `${left} finding${left === 1 ? '' : 's'} waiting on you`, cls: 'text-amber-700 bg-amber-50 border-amber-200' };
  return { label: f.total ? 'All findings responded' : 'Draft report issued · no findings', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
}

export default function MyAudits() {
  const [audits, setAudits] = useState(null);
  const [internal, setInternal] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openInternal, setOpenInternal] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/audit/my-audits`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setAudits(d.success ? (d.audits || []) : []))
      .catch(() => setAudits([]));
    fetch(`${API}/api/audit/my-audits/internal`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setInternal(d.success ? (d.audits || []) : []))
      .catch(() => setInternal([]));
  }, []);

  const loading = audits === null || internal === null;
  const nothing = !loading && audits.length === 0 && internal.length === 0;

  return (
    <div className="h-full flex flex-col bg-[#f8fafc]">
      <header className="flex-shrink-0 bg-white border-b border-slate-200/60 px-8 pt-6 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white shadow-lg">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">My audits</h1>
            <p className="text-slate-400 text-sm mt-0.5">Requests from Compliance that need your response.</p>
          </div>
        </div>
      </header>
      <main className="flex-1 overflow-auto p-8">
        <div className={`${openInternal ? 'max-w-5xl' : 'max-w-3xl'} mx-auto space-y-3`}>
          {openInternal ? (
            <InternalAuditView audit={openInternal} onBack={() => setOpenInternal(null)} />
          ) : openId ? (
            <AuditDetailView auditId={openId} onBack={() => setOpenId(null)} />
          ) : loading ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : nothing ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-10 text-center text-sm text-slate-500">
              No audits involve your department right now.
            </div>
          ) : (<>
            {internal.length > 0 && (
              <div className="space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Audit reports</div>
                {internal.map((a) => {
                  const s = internalStatus(a);
                  return (
                    <button key={a.id} type="button" onClick={() => setOpenInternal(a)} className="w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 text-left hover:border-orange-200">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">{PROGRAM_NAMES[a.program] || String(a.program || '').toUpperCase()} audit · {a.cycle_label}</div>
                        <div className="text-[11px] text-slate-400">
                          {a.report_sent_at && !a.report_final_at && a.response_due ? `Responses due ${fmtDate(a.response_due)}` : a.sent_at ? `Notice sent ${fmtDate(a.sent_at)}` : 'Internal audit'}
                        </div>
                      </div>
                      <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 flex-shrink-0 ${s.cls}`}>{s.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {audits.length > 0 && (
              <div className="space-y-2">
                {internal.length > 0 && <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 pt-2">Requests</div>}
                {audits.map((a) => {
                  const n = Number(a.open_request_count || 0);
                  return (
                    <button key={a.id} type="button" onClick={() => setOpenId(a.id)} className="w-full flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 text-left hover:border-orange-200">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800 truncate">{a.subject_name}</div>
                        <div className="text-[11px] text-slate-400">
                          {a.template_name}
                          {a.evidence_due_date ? ` · documents due ${fmtDate(a.evidence_due_date)}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {n > 0
                          ? <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">{n} waiting on you</span>
                          : <span className="text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">{a.active ? 'Nothing waiting' : 'Wrapped up'}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>)}
        </div>
      </main>
    </div>
  );
}
